import { NormalizedCacheObject } from 'apollo-boost';
import { get, pick } from 'lodash';
import { FileWithPreview } from 'react-dropzone';

import { mapStringToGdAdvTypeInput } from '../mappers/banner/banner';
import { mapBudgetModeToApiFormat, mapListActionToApiFormat } from '../mappers/common/common';
import { mapInternalMarketerAccessTypeFromApi, mapInternalMarketerFromApi } from '../mappers/marketer/internal';
import {
    mapApiFormatToAppData,
    mapApiFormatToAppInfo,
    mapApiFormatToAppsData,
    mapAppSettingsToApiFormat,
    mapAppTrackersUrlTemplateList
} from '../mappers/mobile-apps';
import { mapInternalProductFromApi } from '../mappers/products/internal';
import { getCSRFToken } from '../service/ApiHelpers/ApiHelpers';
import { ApiMethodExecutingError } from '../service/Errors';
import { DeviceType, InterfaceEvents, RumCountersDictKeys } from '../service/RUM/RUM';
import { UrlHelpers } from '../service/UrlHelpers';
import { BannerVideoCreativeApiData } from '../typings/bannersEditState';
import { DayBudgetValue } from '../typings/budget';
import { DropdownChooserItem, Lang, ValidationResultItem } from '../typings/common';
import { DealDetailJava, DealsUpdateObject } from '../typings/deals';
import { AdvType, AdvTypeClient, GroupType } from '../typings/domain';
import { FreelancerAvatar } from '../typings/freelancers';
import { GridAction, GridSourceParams } from '../typings/grid';
import { HeaderInfoBlockApiData, HeaderInfoBlockNewsItemApiData } from '../typings/header';
import { ImportWizardDataRaw, ImportWizardUploadMode } from '../typings/importWizard';
import { InternalProductPlace } from '../typings/internalProductPlace';
import {
    Marketer,
    MarketerProduct,
    ServerMarketer,
    ServerMarketerAccess,
    ServerMarketerProduct
} from '../typings/marketers';
import {
    ApiMobileContentInfoResponse,
    AppFromApiData,
    AppInfo,
    AppSettings,
    JavaMobileAppInfo,
    UrlCheckResponse
} from '../typings/mobile-apps';
import { Product, ServerProduct } from '../typings/products';
import { ReportClientApiData } from '../typings/reportsClient';
import { AppTracker, JavaAppTrackersTemplates } from '../typings/trackingParametersEditor';

import { DNAApolloClient } from './DNAApolloClient/DNAApolloClient';
import { JavaRestApiMethodExecutor } from './JavaRestApiMethodExecutor/JavaRestApiMethodExecutor';
import {
    PerlRestApiBaseResult,
    PerlRestApiMethodExecutor
} from './PerlRestApiMethodExecutor/PerlRestApiMethodExecutor';
import { PythonRestApiMethodExecutor } from './PythonRestApiMethodExecutor/PythonRestApiMethodExecutor';

const EXPORT_TO_EXCEL_URL = '/excel/internal_ad_export';
const IMPORT_FROM_EXCEL_URL = '/excel/internal_ad_upload_excel_file_for_import_and_get_data';

interface PerlRestApiBaseParams {
    cmd: string;
    csrf_token?: string;
}

interface GetKeywordStatShowParams {
    adGroupType: GroupType;
    phrase: string;
    commonMinusPhrases: string[];
    geo: number[];
}

interface GetKeywordsStatShowParams {
    campaignId?: string;
    adGroupId?: string;
    adGroupType: GroupType;
    phrases: string[];
    commonMinusPhrases: string[];
    libraryMinusPhrasesIds?: string[];
    geo: number[];
}

export interface GetPhrasesStatsResponsePhrase {
    searchedAlso: KeywordStatShowItem[];
    searchedWith: KeywordStatShowItem[];
    shows: number;
}

export interface GetPhrasesStatsResponse {
    result: Array<GetPhrasesStatsResponsePhrase>;
    success: boolean;
    text?: string;
}

export interface KeywordStatShowItem {
    phrase: string;
    shows: number;
}

export interface GetKeywordStatShowResponse {
    searchedAlso: KeywordStatShowItem[];
    searchedWith: KeywordStatShowItem[];
    advqErrors: string[];
    success: boolean;
}

export interface RefineMinusWordsParams {
    geo: string;
    phrase: string;
    json_minus_words: string;
}

export interface RefineMinusWordsResponse {
    has_more: 1 | 0;
    words: Array<{
        cnt: number;
        exact_cnt: string;
        word: string;
        phrases: string[];
    }>;
}

export interface AjaxPhraseStatParams {
    geo: string;
    w1: string;
    json_mw1: string;
}

export type AjaxPhraseStatResponse = string;

export interface SaveWelcomeDatаResponse {
    flags?: {
        suggest_yandex_service?: boolean;
        suggest_non_yandex_service?: boolean;
    };
}

export interface JavaApiResponse<T> {
    result: T;
    success: boolean;
    validation_result?: {
        errors: ValidationResultItem[];
        warnings?: ValidationResultItem[];
    };
}

export interface GraphiteTimings {
    timings: Partial<Record<RumCountersDictKeys & 'reqid', number>>;
    reqid?: string;
    interfaceEvents?: InterfaceEvents;
    deviceType: DeviceType;
}

export interface PerlRestApiResult {
    status?: 'success' | 'error';
    result?: number;
    array_errors?: {}[];
    updated_objects?: string[];
    skipped_objects?: string[];
}

export interface GetModerationRejectReasonsResultItem {
    diag_text: string;
    diag_id: string;
    token: string;
    grab_tooltip?: number;
    page_name?: string;
    operator_comment?: string;
    page_id?: string;
    reasons?: Array<GetModerationRejectReasonsResultItem>;
}

export interface ModerationRejectExternalReasonsResult {
    page_name: string;
    page_id: string;
    reasons: Array<GetModerationRejectReasonsResultItem>;
}

export interface GetModerationRejectReasonsResult {
    banner_diags?: {
        banner?: Array<GetModerationRejectReasonsResultItem>;
        phrases?: Array<GetModerationRejectReasonsResultItem>;
        contactinfo?: Array<GetModerationRejectReasonsResultItem>;
        sitelinks_set?: Array<GetModerationRejectReasonsResultItem>;
        image?: Array<GetModerationRejectReasonsResultItem>;
        display_href?: Array<GetModerationRejectReasonsResultItem>;
        video_addition?: Array<GetModerationRejectReasonsResultItem>;
        turbolanding?: Array<GetModerationRejectReasonsResultItem>;
    };
    callout_diags: Array<GetModerationRejectReasonsResultItem>;
    manager_info?: {
        email: string;
    };
    agency_info?: {
        email: string;
        country_region_id: string;
    };
    agency_manager?: {
        login: string;
    };
    bad_banner_pages?: Record<string, ModerationRejectExternalReasonsResult>;
}

export interface UploadImageResultSmartCenter {
    height: number;
    ratio: string;
    width: number;
    x: number;
    y: number;
}

export interface UploadImageResultFormatsApi {
    image_size: {
        height: number;
        width: number;
    };
    path: string;
    smart_centers: Array<UploadImageResultSmartCenter> | null;
}

export type UploadImageResult = {
    hash: string;
    mds_group_id: number;
    mds_namespace: string;
    name: string;
    height: number;
    width: number;
    formats: Array<UploadImageResultFormatsApi>;
};

export type UploadTextImageByLinkResult = {
    hash: string;
    name: string;
};

export interface CountryCurrencyInitialDataResult {
    client_country?: {
        region_id: string;
    };
    countries: Array<{
        region_id: string;
        name_en: string;
        name_ru: string;
        name_tr: string;
        name_uk: string;
    }>;
    main_countries: {
        [key: string]: number;
    };
    countries_currencies: {
        [key: string]: string[];
    };
}

type ExecuteGroupsGridActionParams = PerlRestApiBaseParams & {
    adgroup_ids: string;
    ulogin: string;
    cid?: string;
};

// todo переделать взаимодействие с API на эпики DIRECT-79145
export class RestApiManager {
    private perlApi: PerlRestApiMethodExecutor;
    private javaApi: JavaRestApiMethodExecutor;
    private pythonApi: PythonRestApiMethodExecutor;

    constructor(
        perlApi: PerlRestApiMethodExecutor,
        javaApi: JavaRestApiMethodExecutor,
        pythonApi: PythonRestApiMethodExecutor
    ) {
        this.perlApi = perlApi;
        this.javaApi = javaApi;
        this.pythonApi = pythonApi;
    }

    public setApolloClient(dnaApolloClient: DNAApolloClient<NormalizedCacheObject>) {
        this.perlApi.setApolloClient(dnaApolloClient);
    }

    async switchToOldInterface(params?: GridSourceParams) {
        const requestUrl = 'feature/set_state';

        const requestParams = {
            body: JSON.stringify([{ feature: 'grid', enabled: false }])
        };

        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
        }>(requestUrl, requestParams);

        if (response.success === true && params && params.login) {
            window.location.href = UrlHelpers.getDirectLink('showCamps', {
                ulogin: params.login
            });
        }
    }

    getKeywordStatShow({
        adGroupType,
        commonMinusPhrases,
        phrase,
        geo
    }: GetKeywordStatShowParams): Promise<GetKeywordStatShowResponse> {
        const requestURL = '/public/keyword_stat_shows';

        const requestParams = {
            body: JSON.stringify({
                geo: geo.join(','),
                phrase,
                commonMinusPhrases,
                adGroupType
            })
        };

        return this.javaApi
            .executeApiMethod<{
                keywordStat?: GetKeywordStatShowResponse;
            }>(requestURL, requestParams)
            .then(response => response as GetKeywordStatShowResponse);
    }

    async getPhrasesStats({
        commonMinusPhrases,
        phrases,
        geo,
        campaignId,
        adGroupId,
        adGroupType,
        libraryMinusPhrasesIds
    }: GetKeywordsStatShowParams): Promise<Array<GetPhrasesStatsResponsePhrase>> {
        const requestURL = '/public/bulk_keyword_stat_shows';

        const requestParams = {
            body: JSON.stringify({
                campaignId: campaignId ? Number(campaignId) : null,
                adGroupId: adGroupId ? Number(adGroupId) : null,
                adGroupType,
                libraryMinusPhrasesIds: libraryMinusPhrasesIds || [],
                geo: geo.join(','),
                phrases,
                commonMinusPhrases
            })
        };

        const response = await this.javaApi.executeApiMethod<GetPhrasesStatsResponse>(requestURL, requestParams);

        if (!response.success) {
            throw new ApiMethodExecutingError({ message: response.text || '' });
        }

        return response.result;
    }

    async getAppsData(login: string) {
        const requestUrl = `mobile_app/list?ulogin=${login}`;

        const requestParams = { method: 'GET' };

        const response = await this.javaApi.executeApiMethod<{
            result: Array<JavaMobileAppInfo>;
            success: boolean;
        }>(requestUrl, requestParams);

        const apps = response.result || [];

        return {
            data: mapApiFormatToAppsData(apps),
            allItemsIds: [],
            total: apps.length
        };
    }
    async getExternalReasonsText(outdoorIds: Array<string>) {
        const requestUrl = `moderationreason/showModReasons`;
        const requestParams = {
            method: 'POST',
            body: JSON.stringify({
                ids: outdoorIds,
                tld: UrlHelpers.getTld()
            })
        };
        const response = await this.javaApi.executeApiMethod<{
            errors: Array<string>;
            reasons: Record<string, string>;
        }>(requestUrl, requestParams);

        return response;
    }

    async getAppData(id: string, login: string): Promise<AppFromApiData> {
        const requestUrl = `mobile_app/get?ulogin=${login}&id=${id}`;

        const requestParams = { method: 'GET' };

        const response = await this.javaApi.executeApiMethod<{
            result: JavaMobileAppInfo;
            success: boolean;
            text?: string;
        }>(requestUrl, requestParams);

        if (!response.success) {
            throw new ApiMethodExecutingError({ message: response.text || '' });
        }
        return mapApiFormatToAppData(response.result);
    }

    /* Запросы для ЦМ */

    async getInternalProducts(): Promise<Product[]> {
        const requestUrl = 'internalads/products/list';
        const requestParams = {
            method: 'GET'
        };

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<ServerProduct[]>>(
            requestUrl,
            requestParams
        );

        return result.map(mapInternalProductFromApi);
    }

    async createInternalProduct(productName: string, description: string): Promise<Product> {
        const requestUrl = 'internalads/products/create';
        const requestParams = {
            method: 'POST',
            body: JSON.stringify({
                productName: productName,
                productDescription: description
            })
        };

        const { result, success, validation_result } = await this.javaApi.executeApiMethod<
            JavaApiResponse<ServerProduct>
        >(requestUrl, requestParams);

        if (!success && validation_result && validation_result.errors) {
            throw new ApiMethodExecutingError({ message: 'Internal Error', errors: validation_result.errors });
        }

        return mapInternalProductFromApi(result);
    }

    async updateInternalProductDescription(id: number, description: string): Promise<Product> {
        const requestUrl = 'internalads/products/update';
        const requestParams = {
            method: 'POST',
            body: JSON.stringify({
                productId: id,
                productDescription: description
            })
        };

        const { result, success, validation_result } = await this.javaApi.executeApiMethod<
            JavaApiResponse<ServerProduct>
        >(requestUrl, requestParams);

        if (!success && validation_result && validation_result.errors) {
            throw new ApiMethodExecutingError({ message: '1', errors: validation_result.errors });
        }

        return mapInternalProductFromApi(result);
    }

    async getInternalMarketers(): Promise<Marketer[]> {
        const requestUrl = 'internalads/managers/list';

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<ServerMarketer[]>>(requestUrl, {
            method: 'GET'
        });

        return result.map(mapInternalMarketerFromApi);
    }

    async getInternalProductPlaces(): Promise<InternalProductPlace[]> {
        const requestUrl = 'internalads/places/list';

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<InternalProductPlace[]>>(requestUrl, {
            method: 'GET'
        });

        return result;
    }

    async getInternalMarketer(marketerId: string): Promise<Marketer> {
        const requestUrl = `internalads/managers/list?clientId=${marketerId}`;

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<ServerMarketer>>(requestUrl, {
            method: 'GET'
        });

        return mapInternalMarketerFromApi(result);
    }

    async addInternalMarketerProductAccess(
        marketerId: string,
        productId: string,
        accessType: ServerMarketerAccess,
        placeIds: number[]
    ): Promise<MarketerProduct> {
        const requestBody: Pick<ServerMarketerProduct, 'productId' | 'accessType' | 'placeIds'> = {
            accessType: accessType === ServerMarketerAccess.BY_PLACE ? ServerMarketerAccess.FULL : accessType,
            placeIds: accessType === ServerMarketerAccess.BY_PLACE ? placeIds : [],
            productId: Number(productId)
        };
        const requestUrl = `internalads/managers/add-access?clientId=${marketerId}`;
        const requestParams = {
            method: 'POST',
            body: JSON.stringify(requestBody)
        };

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<ServerMarketerProduct>>(
            requestUrl,
            requestParams
        );

        return {
            id: result.productId.toString(),
            productName: result.productName,
            productLogin: result.productLogin,
            access: mapInternalMarketerAccessTypeFromApi(result.accessType, result.placeIds),
            placeIds: result.placeIds || [],
            isRemoving: false,
            isSaved: true
        };
    }

    async updateInternalMarketerProductAccess(
        marketerId: string,
        productId: string,
        accessType: ServerMarketerAccess,
        placeIds: number[]
    ): Promise<MarketerProduct> {
        const requestBody: Pick<ServerMarketerProduct, 'productId' | 'accessType' | 'placeIds'> = {
            accessType: accessType === ServerMarketerAccess.BY_PLACE ? ServerMarketerAccess.FULL : accessType,
            placeIds: accessType === ServerMarketerAccess.BY_PLACE ? placeIds : [],
            productId: Number(productId)
        };
        const requestUrl = `internalads/managers/update_access?clientId=${marketerId}`;
        const requestParams = {
            method: 'POST',
            body: JSON.stringify(requestBody)
        };

        const { result } = await this.javaApi.executeApiMethod<JavaApiResponse<ServerMarketerProduct>>(
            requestUrl,
            requestParams
        );

        return {
            id: result.productId.toString(),
            productName: result.productName,
            productLogin: result.productLogin,
            access: mapInternalMarketerAccessTypeFromApi(result.accessType, result.placeIds),
            placeIds: result.placeIds || [],
            isRemoving: false,
            isSaved: true
        };
    }

    async removeInternalMarketerProductAccess(marketerId: string, productId: string): Promise<boolean> {
        const requestUrl = `internalads/managers/remove-access?clientId=${marketerId}`;
        const requestParams = {
            method: 'POST',
            body: JSON.stringify([{ productId: Number(productId) }])
        };

        const { success } = await this.javaApi.executeApiMethod<{ success: boolean }>(requestUrl, requestParams);

        return success;
    }

    /* Делает запрос на выполнение массового действия над списком элементов таблицы кампаний */
    executeCampaignsGridItemsAction(
        action: GridAction,
        itemsIds: Array<string>,
        login: string
    ): Promise<{ status?: 'success' }> {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & { cid: string; ulogin: string },
            PerlRestApiBaseResult & { status?: 'success' }
        >({
            methodPath: 'main.pl',
            params: {
                cmd: mapListActionToApiFormat(action),
                cid: itemsIds.join(','),
                ulogin: login
            }
        });
    }

    async fetchCountryCurrencyInitialData(): Promise<CountryCurrencyInitialDataResult> {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & { cmd: string },
            PerlRestApiBaseResult & CountryCurrencyInitialDataResult
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'chooseCountryCurrency'
            }
        });
    }

    /* Делает запрос на выполнение массового действия над списком элементов таблицы групп */
    async executeGroupsGridItemsAction(
        action: GridAction,
        itemsIds: Array<string>,
        login: string,
        cid?: string
    ): Promise<PerlRestApiResult> {
        const params: ExecuteGroupsGridActionParams = {
            cmd: mapListActionToApiFormat(action),
            adgroup_ids: itemsIds.join(','),
            ulogin: login
        };

        if (cid !== undefined) {
            params.cid = cid;
        }

        return this.perlApi.executeApiMethod<ExecuteGroupsGridActionParams, PerlRestApiBaseResult & PerlRestApiResult>({
            methodPath: 'main.pl',
            params
        });
    }

    async executeDnaGroupsGridItemsAction(
        action: GridAction,
        itemsIds: Array<string>,
        login: string
    ): Promise<PerlRestApiResult> {
        return this.perlApi.executeAwaitApiMethod<
            ExecuteGroupsGridActionParams,
            PerlRestApiBaseResult & PerlRestApiResult
        >({
            methodPath: 'main.pl',
            params: {
                cmd: mapListActionToApiFormat(action),
                adgroup_ids: itemsIds.join(','),
                ulogin: login
            },
            login
        });
    }

    ajaxPhraseStat({ geo, phrase, minusWords }: { geo: number[]; phrase: string; minusWords: string[] }) {
        return this.perlApi.executeApiMethod<PerlRestApiBaseParams & AjaxPhraseStatParams, AjaxPhraseStatResponse>({
            methodPath: 'main.pl',
            params: {
                cmd: 'ajaxPhraseStat',
                geo: geo.join(','),
                w1: phrase,
                json_mw1: JSON.stringify(minusWords)
            }
        });
    }

    ajaxRefineMinusWords({ geo, phrase, minusWords }: { geo: number[]; phrase: string; minusWords: string[] }) {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & RefineMinusWordsParams,
            PerlRestApiBaseResult & RefineMinusWordsResponse
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'ajaxRefineMinusWords',
                geo: geo.join(','),
                phrase,
                json_minus_words: JSON.stringify(minusWords)
            }
        });
    }

    async getModerationRejectReasons(
        bid: string,
        additionsIds: Array<string> = []
    ): Promise<PerlRestApiBaseResult & GetModerationRejectReasonsResult> {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & { bid: string; additions_item_id: string },
            PerlRestApiBaseResult & GetModerationRejectReasonsResult
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'showDiag',
                additions_item_id: additionsIds.join(','),
                bid: bid
            }
        });
    }

    /*
     * Получение информации о приложении по ссылке
     */
    async getAppInfo(link: string, login: string) {
        const requestUrl = `mobile_app/mobile_content?ulogin=${login}&url=${encodeURIComponent(link)}`;

        const requestParams = { method: 'GET' };

        const response = await this.javaApi.executeApiMethod<{
            result: ApiMobileContentInfoResponse;
            success: boolean;
            text: string;
            validation_result?: {
                errors: ValidationResultItem[];
                warnings?: ValidationResultItem[];
            };
        }>(requestUrl, requestParams);

        return mapApiFormatToAppInfo(response.result || {}, link, [
            {
                path: 'storeHref',
                code: response.text ? 'MobileAppDefects.Gen.INVALID_URL' : '',
                params: null
            }
        ]);
    }

    async getMultipleAppsInfo(links: string[], login: string): Promise<AppInfo[]> {
        return Promise.all(links.map(link => this.getAppInfo(link, login)));
    }

    /*
     * Получение информации о приложении по ссылке
     */
    async getTrackerUrlTemplateList(login: string) {
        const requestUrl = `tracker_url_template/list?ulogin=${login}`;

        const requestParams = { method: 'GET' };

        const response = await this.javaApi.executeApiMethod<{
            result: JavaAppTrackersTemplates;
            success: boolean;
        }>(requestUrl, requestParams);

        return mapAppTrackersUrlTemplateList(response.result);
    }

    async createMobileApp(login: string, appSettings: AppSettings, tracker: AppTracker) {
        const requestUrl = `mobile_app/create?ulogin=${login}`;

        const requestParams = {
            method: 'POST',
            body: JSON.stringify(mapAppSettingsToApiFormat(appSettings, tracker))
        };

        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
            validation_result?: {
                errors: ValidationResultItem[];
            };
        }>(requestUrl, requestParams);

        const errors = get(response, 'validation_result.errors', []).map((error: ValidationResultItem) =>
            pick(error, ['code', 'path'])
        );

        return {
            errors,
            success: response.success
        };
    }

    async updateMobileApp(login: string, appSettings: AppSettings, tracker: AppTracker) {
        const requestUrl = `mobile_app/update?ulogin=${login}`;

        const requestParams = {
            method: 'POST',
            body: JSON.stringify(mapAppSettingsToApiFormat(appSettings, tracker))
        };

        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
            validation_result?: {
                errors: ValidationResultItem[];
            };
        }>(requestUrl, requestParams);

        const errors = get(response, 'validation_result.errors', []).map((error: ValidationResultItem) =>
            pick(error, ['code', 'path'])
        );

        return {
            errors,
            success: response.success
        };
    }

    /*
     * Делает запрос на сохранение ограничения дневного бюджета для кампании
     */
    async saveCampaignDayBudget(
        value: DayBudgetValue,
        campaignId: string,
        login: string
    ): Promise<PerlRestApiBaseResult> {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & {
                ulogin: string;
                cid: string;
                json_day_budget: string;
            },
            PerlRestApiBaseResult
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'ajaxSaveDayBudget',
                ulogin: login,
                cid: String(campaignId),
                json_day_budget: JSON.stringify({
                    set: value.isActive,
                    sum: value.sum,
                    show_mode: mapBudgetModeToApiFormat(value.mode)
                })
            }
        });
    }

    async fetchReports(
        ulogin?: string
    ): Promise<{
        success?: boolean;
        result?: Array<ReportClientApiData>;
        validation_result?: { errors?: string[] };
    }> {
        const requestUrl = ulogin ? `agency_offline_report/list?ulogin=${ulogin}` : `agency_offline_report/list`;

        const result = await this.javaApi.executeApiMethod<{
            success?: boolean;
            result?: Array<ReportClientApiData>;
            validation_result?: { errors?: string[] };
        }>(requestUrl, { method: 'GET' });

        if (!result.success && result.validation_result && result.validation_result.errors) {
            throw new ApiMethodExecutingError({ message: result.validation_result.errors[0] });
        }

        return result;
    }

    async addReport(
        ulogin?: string,
        dates?: {
            dateStart: string;
            dateEnd: string;
        }
    ): Promise<{
        success?: boolean;
        validation_result?: { errors?: string[] };
    }> {
        const requestUrl = ulogin ? `agency_offline_report/enqueue?ulogin=${ulogin}` : `agency_offline_report/enqueue`;

        const result = await this.javaApi.executeApiMethod<{
            success?: boolean;
            validation_result?: { errors?: string[] };
        }>(requestUrl, {
            body: JSON.stringify(dates)
        });

        if (!result.success && result.validation_result && result.validation_result.errors) {
            throw new ApiMethodExecutingError({ message: result.validation_result.errors[0] });
        }

        return result;
    }

    saveWelcomePageData(email?: string, country?: string, currency?: string, isAccepted?: boolean, isTouch?: boolean) {
        return this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & {
                json_client_data: string;
            },
            PerlRestApiBaseResult & SaveWelcomeDatаResponse
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'ajaxCreateClient',
                json_client_data: JSON.stringify({
                    country: Number(country),
                    email: email || '',
                    gdpr_agreement_accepted: isAccepted ? 1 : 0,
                    currency,
                    is_touch: isTouch ? 1 : 0
                })
            }
        });
    }

    async urlCheck(url?: string): Promise<UrlCheckResponse> {
        // Временно, исправить в DIRECT-81888
        if (!url) {
            return {
                isValid: true
            };
        }

        const response = await this.perlApi.executeApiMethod<
            PerlRestApiBaseParams & {
                json_data: string;
            },
            PerlRestApiBaseResult
        >({
            methodPath: 'main.pl',
            params: {
                cmd: 'ajaxCheckUrlMass',
                json_data: JSON.stringify([
                    {
                        url: encodeURI(url),
                        url_protocol: 'https://',
                        is_tracking_href: '1',
                        requestId: 1
                    }
                ])
            }
        });

        return {
            isValid: !!response[0].code,
            errorMessage: response[0].text
        };
    }

    public sendToGraffit(
        data: Partial<Record<RumCountersDictKeys, number>>,
        interfaceEvents: InterfaceEvents,
        deviceType: DeviceType,
        reqId?: string
    ) {
        const requestUrl = 'log/frontendTimings';
        const graphiteTimings: GraphiteTimings = {
            timings: { ...data },
            reqid: reqId,
            interfaceEvents,
            deviceType
        };

        this.javaApi
            .executeApiMethod<{
                success?: boolean;
                validation_result?: { errors?: string[] };
            }>(requestUrl, {
                body: JSON.stringify(graphiteTimings)
            })
            .catch(() => {
                // Ошибки отправки времени в графану игнорируем
                return;
            });
    }

    async getDetailDeals(login: string, dealIds: number[]) {
        const requestUrl = `deal/details?ulogin=${login}`;
        const requestParams = {
            body: JSON.stringify(dealIds)
        };

        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
            result: DealDetailJava[];
        }>(requestUrl, requestParams);

        return response.success ? response.result : [];
    }

    async updateDeals(login: string, data: DealsUpdateObject) {
        const requestUrl = `deal/update_deals?ulogin=${login}`;
        const requestParams = { body: JSON.stringify(data) };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<DealsUpdateObject>>(
            requestUrl,
            requestParams
        );

        // DIRECT-77860: warnings в нашем случае должны восприниматься как ошибка, детали в задаче
        const errors = get(response, 'validation_result.errors', [])
            .concat(get(response, 'validation_result.warnings', []))
            .map((error: ValidationResultItem) => pick(error, ['code', 'path', 'params']));

        return {
            errors,
            success: response.success,
            result: response.result
        };
    }

    async acceptDeals(login: string, dealIds: number[]) {
        const requestUrl = `/deal/activate?ulogin=${login}`;
        const requestParams = { body: JSON.stringify(dealIds) };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<number[]>>(requestUrl, requestParams);

        return {
            errors: get(response, 'validation_result.errors', []),
            success: response.success
        };
    }

    async rejectDeals(login: string, dealIds: number[]) {
        const requestUrl = `/deal/complete?ulogin=${login}`;
        const requestParams = { body: JSON.stringify(dealIds) };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<number[]>>(requestUrl, requestParams);

        return {
            errors: get(response, 'validation_result.errors', []),
            success: response.success
        };
    }

    async searchSubClientsForDeals(login: string): Promise<string[]> {
        const requestUrl = `agency/search_sub_clients_for_deals?ulogin=${login}`;

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<string[]>>(requestUrl, {});

        return response.success ? response.result : [];
    }

    async searchCampaignsForDeals(login: string): Promise<DropdownChooserItem[]> {
        const requestUrl = `campaign/search_deal_campaigns?ulogin=${login}`;
        const requestParams = { method: 'GET' };

        const response = await this.javaApi.executeApiMethod<
            JavaApiResponse<
                {
                    name: string;
                    id: number;
                    deal_ids: number[];
                    status_moderate: string;
                } & DropdownChooserItem[]
            >
        >(requestUrl, requestParams);

        return response.success ? response.result : [];
    }

    // Поумолчанию отправка с текущего состояния страницы
    async sendGridFeedback(message: string, currentUrl: string = window.location.href) {
        const requestUrl = 'feedback/grid';
        const requestParams = {
            method: 'POST',
            body: JSON.stringify({
                message,
                url: currentUrl
            })
        };

        await this.javaApi.executeApiMethod<JavaApiResponse<{}>>(requestUrl, requestParams);
    }

    async getVideoDataByCreativeIds(creativeIds: Array<string>, ulogin: string) {
        const requestUrl = `creative/search/video?ulogin=${ulogin}&creative_ids=${creativeIds.join(',')}`;
        const requestParams = {
            method: 'GET'
        };

        return await this.javaApi.executeApiMethod<JavaApiResponse<Array<BannerVideoCreativeApiData>>>(
            requestUrl,
            requestParams
        );
    }

    async getCreativeDataByCreativeIds(creativeIds: Array<string>, ulogin: string) {
        return await this.perlApi.executeApiMethod({
            methodPath: 'main.pl',
            params: {
                cmd: 'searchCanvasCreatives',
                ulogin,
                creative_id: creativeIds,
                csrf_token: getCSRFToken()
            }
        });
    }

    fetchNewsData(uid: string, lang: Lang, euid: string): Promise<HeaderInfoBlockApiData> {
        return this.pythonApi.executeApiMethod<{}, HeaderInfoBlockApiData>({
            methodPath: '',
            params: {
                query: {
                    cmd: 'ajaxInfoblockRequest'
                },
                body: JSON.stringify({
                    lang,
                    euid: euid,
                    uid: uid,
                    queue: [
                        {
                            data: {},
                            type: 'require-events',
                            uniqId: ''
                        }
                    ],
                    csrf_token: getCSRFToken()
                })
            }
        });
    }

    fetchNewsDataById(id: string): Promise<HeaderInfoBlockNewsItemApiData> {
        return this.pythonApi.executeApiMethod<{}, HeaderInfoBlockNewsItemApiData>({
            methodPath: '',
            params: {
                query: {
                    cmd: 'ajaxGetNewsById',
                    ext_news_id: id,
                    csrf_token: getCSRFToken()
                }
            }
        });
    }

    async uploadFreelancerImage(login: string, blob: Blob): Promise<FreelancerAvatar> {
        const requestUrl = `/freelancer/upload_avatar?ulogin=${login}`;

        const body: FormData = new FormData();
        body.append('file', blob, 'image');

        const requestParams = {
            method: 'POST',
            body,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
            result: {
                key: string;
                url: string;
            };
        }>(requestUrl, requestParams);

        if (response.success) {
            return {
                id: response.result.key,
                url: response.result.url
            };
        } else {
            throw new ApiMethodExecutingError({ message: 'image upload failed' });
        }
    }

    async checkMassUrl(login: string, urls: Array<string>) {
        const requestUrl = `ad/check_url?ulogin=${login}`;
        const requestParams = {
            method: 'POST',
            body: JSON.stringify(urls)
        };

        return await this.javaApi.executeApiMethod<{
            check_results: {
                result: boolean;
                domain: string;
                error?: string;
            }[];
            validation_errors: {
                code: string;
            }[];
        }>(requestUrl, requestParams);
    }

    async uploadAdBannerImage(
        login: string,
        bannerType: AdvType,
        files: FileWithPreview[]
    ): Promise<JavaApiResponse<UploadImageResult[]>> {
        const requestUrl = `/image/upload?ulogin=${login}`;

        const body: FormData = new FormData();
        // body.append('ulogin', login);
        body.append('ad_type', mapStringToGdAdvTypeInput(bannerType));

        files.forEach((file, i) => {
            body.append(`files`, file, file.name);
        });

        const requestParams = {
            method: 'POST',
            body,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<UploadImageResult[]>>(
            requestUrl,
            requestParams
        );

        if (response.success) {
            return response;
        } else {
            const validationResult = response.validation_result;
            throw new ApiMethodExecutingError({
                message: '',
                errors: ((validationResult && validationResult.errors) || []) as ValidationResultItem[],
                warnings: ((validationResult && validationResult!.warnings) || []) as ValidationResultItem[]
            });
        }
    }

    async uploadImportFile(
        login: string,
        file: File,
        importMode: ImportWizardUploadMode
    ): Promise<ImportWizardDataRaw> {
        const requestUrl = `${IMPORT_FROM_EXCEL_URL}?ulogin=${login}&importMode=${importMode}`;
        const body: FormData = new FormData();

        body.set('excelFile', file, file.name);

        const requestParams = {
            method: 'POST',
            body,
            headers: {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<ImportWizardDataRaw>>(
            requestUrl,
            requestParams
        );

        if (response.success) {
            return response.result;
        }

        const validationResult = response.validation_result;

        throw new ApiMethodExecutingError({
            message: '',
            errors: (validationResult && validationResult.errors) || [],
            warnings: (validationResult && validationResult!.warnings) || []
        });
    }

    // todo https://st.yandex-team.ru/DIRECT-110042
    async uploadImportImages(): Promise<void> {
        await new Promise(resolve => setTimeout(resolve, 500));
    }

    async uploadBannerTextImageByLink(
        login: string,
        link: string
    ): Promise<{
        success: boolean;
        result: UploadTextImageByLinkResult;
    }> {
        const requestUrl = `/image/text/upload_by_url?ulogin=${login}&url=${encodeURIComponent(link)}`;

        const requestParams = {
            method: 'POST',
            headers: {
                Accept: 'application/json;charset=UTF-8',
                'Content-Type': 'application/json;charset=UTF-8',
                'X-TVM-Allow-All': 'true',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<UploadTextImageByLinkResult>>(
            requestUrl,
            requestParams
        );

        if (response.success) {
            return response;
        } else {
            const validationResult = response.validation_result;
            throw new ApiMethodExecutingError({
                message: '',
                errors: validationResult!.errors as ValidationResultItem[],
                warnings: validationResult!.warnings as ValidationResultItem[]
            });
        }
    }

    async uploadBannerAdImageByLink(
        login: string,
        bannerType: AdvType | AdvTypeClient,
        link: string
    ): Promise<{
        success: boolean;
        result: UploadImageResult;
    }> {
        const requestUrl = `/image/upload_by_url?ulogin=${login}&ad_type=${mapStringToGdAdvTypeInput(
            bannerType
        )}&url=${encodeURIComponent(link)}`;

        const requestParams = {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json;charset=UTF-8',
                'X-TVM-Allow-All': 'true',
                'X-Requested-With': 'XMLHttpRequest'
            }
        };

        const response = await this.javaApi.executeApiMethod<JavaApiResponse<UploadImageResult>>(
            requestUrl,
            requestParams
        );

        if (response.success) {
            return response;
        } else {
            const validationResult = response.validation_result;
            throw new ApiMethodExecutingError({
                message: '',
                errors: validationResult!.errors as ValidationResultItem[],
                warnings: validationResult!.warnings as ValidationResultItem[]
            });
        }
    }

    /* xls data api*/
    async exportXls(
        requestParams: RequestInit,
        login: string
    ): Promise<{ success: boolean; result: { exportFileUrl: string } }> {
        const requestUrl = `${EXPORT_TO_EXCEL_URL}?ulogin=${login}`;
        const response = await this.javaApi.executeApiMethod<{
            success: boolean;
            result: { exportFileUrl: string };
        }>(requestUrl, requestParams);

        return response;
    }
}
